import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCommercialOperationTypes, updateProjectStep2, getProject } from '../../services/api';

const Step2DevelopmentParameters = ({ project, onComplete, onBack }) => {
  const navigate = useNavigate();
  const [commercialTypes, setCommercialTypes] = useState([]);
  const [commercialComponents, setCommercialComponents] = useState([
    { operation_type: '', gfa: '' }
  ]);
  const [formData, setFormData] = useState({
    dwelling_count: '',
    one_bedroom_units: '',
    two_bedroom_units: '',
    three_plus_bedroom_units: '',
    operating_days_per_week: 7
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isDevelopmentTypeSelected = project?.development_type;
  const isCommercial = project?.development_type === 'Commercial';
  const isResidential = project?.development_type === 'Residential';
  const isMixedUse = project?.development_type === 'Mixed use';

  useEffect(() => {
    if (project?.state_code) {
      loadCommercialTypes(project.state_code);
    } else {
      loadCommercialTypes('VIC'); // Default to VIC
    }
    if (project?.step2_data) {
      // Load commercial components if they exist
      if (project.step2_data.commercial_components && project.step2_data.commercial_components.length > 0) {
        setCommercialComponents(project.step2_data.commercial_components);
      }
      
      setFormData({
        dwelling_count: project.step2_data.dwelling_count || '',
        one_bedroom_units: project.step2_data.one_bedroom_units || '',
        two_bedroom_units: project.step2_data.two_bedroom_units || '',
        three_plus_bedroom_units: project.step2_data.three_plus_bedroom_units || '',
        operating_days_per_week: project.step2_data.operating_days_per_week || 7
      });
    }
  }, [project]);

  const loadCommercialTypes = async (stateCode = 'VIC') => {
    try {
      const types = await getCommercialOperationTypes(stateCode);
      setCommercialTypes(types);
    } catch (error) {
      console.error('Failed to load commercial types:', error);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleCommercialComponentChange = (index, field, value) => {
    const updated = [...commercialComponents];
    updated[index][field] = value;
    setCommercialComponents(updated);
  };

  const addCommercialComponent = () => {
    setCommercialComponents([...commercialComponents, { operation_type: '', gfa: '' }]);
  };

  const removeCommercialComponent = (index) => {
    if (commercialComponents.length > 1) {
      const updated = commercialComponents.filter((_, i) => i !== index);
      setCommercialComponents(updated);
    }
  };

  const getTotalCommercialGFA = () => {
    return commercialComponents.reduce((total, component) => {
      return total + (parseFloat(component.gfa) || 0);
    }, 0);
  };

  useEffect(() => {
    if (isResidential || isMixedUse) {
      const total = 
        (parseInt(formData.one_bedroom_units) || 0) +
        (parseInt(formData.two_bedroom_units) || 0) +
        (parseInt(formData.three_plus_bedroom_units) || 0);
      if (total !== formData.dwelling_count) {
        setFormData(prev => ({ ...prev, dwelling_count: total || '' }));
      }
    }
  }, [formData.one_bedroom_units, formData.two_bedroom_units, formData.three_plus_bedroom_units, isResidential, isMixedUse]);

  const validateForm = () => {
    if (isCommercial || isMixedUse) {
      // Validate commercial components
      for (let i = 0; i < commercialComponents.length; i++) {
        const component = commercialComponents[i];
        if (!component.operation_type) {
          setError(`Please select an operation type for commercial component ${i + 1}`);
          return false;
        }
        if (!component.gfa || parseFloat(component.gfa) <= 0) {
          setError(`Please enter a valid GFA (greater than 0) for commercial component ${i + 1}`);
          return false;
        }
        if (parseFloat(component.gfa) > 100000) {
          setError(`GFA for commercial component ${i + 1} seems unusually large. Please verify the value.`);
          return false;
        }
      }
    }

    if (isResidential || isMixedUse) {
      const oneBed = parseInt(formData.one_bedroom_units) || 0;
      const twoBed = parseInt(formData.two_bedroom_units) || 0;
      const threePlusBed = parseInt(formData.three_plus_bedroom_units) || 0;
      const total = oneBed + twoBed + threePlusBed;
      
      // Check for negative values
      if (oneBed < 0 || twoBed < 0 || threePlusBed < 0) {
        setError('Dwelling unit counts cannot be negative');
        return false;
      }
      
      // Check for unreasonably large values
      if (total > 1000) {
        setError('Total dwelling count seems unusually large (over 1000 units). Please verify the values.');
        return false;
      }
      
      if (total === 0) {
        setError(isMixedUse ? 'Please enter at least one dwelling unit for residential component' : 'Please enter at least one dwelling unit');
        return false;
      }
    }

    if (formData.operating_days_per_week < 1 || formData.operating_days_per_week > 7) {
      setError('Operating days must be between 1 and 7');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let step2Data = {
        operating_days_per_week: formData.operating_days_per_week
      };

      if (isCommercial || isMixedUse) {
        // Filter out empty components and calculate total
        const validComponents = commercialComponents.filter(c => c.operation_type && c.gfa);
        step2Data.commercial_components = validComponents.map(c => ({
          operation_type: c.operation_type,
          gfa: parseFloat(c.gfa)
        }));
        step2Data.total_commercial_gfa = getTotalCommercialGFA();
      }

      if (isResidential || isMixedUse) {
        step2Data.dwelling_count = formData.dwelling_count;
        step2Data.one_bedroom_units = parseInt(formData.one_bedroom_units) || 0;
        step2Data.two_bedroom_units = parseInt(formData.two_bedroom_units) || 0;
        step2Data.three_plus_bedroom_units = parseInt(formData.three_plus_bedroom_units) || 0;
      }

      await updateProjectStep2(project.id, step2Data);
      
      const updatedProject = await getProject(project.id);
      onComplete(updatedProject);
    } catch (error) {
      console.error('Error saving step 2:', error);
      setError(error.response?.data?.detail || 'Failed to save development parameters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isDevelopmentTypeSelected) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-yellow-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Development Type Not Selected</h2>
        <p className="text-gray-600 mb-4">Please go back to Step 1 and select a development type.</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Go Back to Step 1
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Step 2: Development Parameters</h2>
        <p className="mt-1 text-sm text-gray-600">
          Provide details about your {project.development_type.toLowerCase()} development
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {(isCommercial || isMixedUse) && (
          <div className="border-b border-gray-200 pb-6">
            {isMixedUse && (
              <h3 className="text-lg font-medium text-gray-900 mb-4">Commercial Component(s)</h3>
            )}
            
            {/* Commercial GFA Note */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Commercial GFA Note:</span> Where a single building contains many different internal functions (such as a Hotel), break up and record the GFA into grouped types. For example, Food &amp; Beverage areas would be recorded as a total under Restaurant/Cafe.
              </p>
            </div>
            
            <div className="space-y-6">
              {commercialComponents.map((component, index) => (
                <div key={index} className="p-4 border border-gray-300 rounded-lg bg-gray-50">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      Commercial Component {index + 1}
                    </h4>
                    {commercialComponents.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCommercialComponent(index)}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor={`operation_type_${index}`} className="block text-sm font-medium text-gray-700">
                        Commercial Operation Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        id={`operation_type_${index}`}
                        required={isCommercial || isMixedUse}
                        value={component.operation_type}
                        onChange={(e) => handleCommercialComponentChange(index, 'operation_type', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="">Select operation type...</option>
                        {commercialTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor={`gfa_${index}`} className="block text-sm font-medium text-gray-700">
                        Gross Floor Area (GFA) - m² <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        id={`gfa_${index}`}
                        required={isCommercial || isMixedUse}
                        min="1"
                        step="0.01"
                        value={component.gfa}
                        onChange={(e) => handleCommercialComponentChange(index, 'gfa', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                        placeholder="e.g., 500"
                      />
                      <p className="mt-1 text-sm text-gray-500">Floor area for this component in square meters</p>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addCommercialComponent}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Another Commercial Component
              </button>

              {getTotalCommercialGFA() > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Total Commercial GFA: {getTotalCommercialGFA().toFixed(0)} m²
                      </p>
                      <p className="text-xs text-blue-700">
                        Calculated from all commercial components
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {(isResidential || isMixedUse) && (
          <div className="border-b border-gray-200 pb-6">
            {isMixedUse && (
              <h3 className="text-lg font-medium text-gray-900 mb-4">Residential Component</h3>
            )}
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="one_bedroom_units" className="block text-sm font-medium text-gray-700">
                    1 Bedroom Units
                  </label>
                  <input
                    type="number"
                    id="one_bedroom_units"
                    name="one_bedroom_units"
                    min="0"
                    value={formData.one_bedroom_units}
                    onChange={handleChange}
                    autoComplete="off"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label htmlFor="two_bedroom_units" className="block text-sm font-medium text-gray-700">
                    2 Bedroom Units
                  </label>
                  <input
                    type="number"
                    id="two_bedroom_units"
                    name="two_bedroom_units"
                    min="0"
                    value={formData.two_bedroom_units}
                    onChange={handleChange}
                    autoComplete="off"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label htmlFor="three_plus_bedroom_units" className="block text-sm font-medium text-gray-700">
                    3+ Bedroom Units
                  </label>
                  <input
                    type="number"
                    id="three_plus_bedroom_units"
                    name="three_plus_bedroom_units"
                    min="0"
                    value={formData.three_plus_bedroom_units}
                    onChange={handleChange}
                    autoComplete="off"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Total Dwellings: {formData.dwelling_count || 0}
                    </p>
                    <p className="text-xs text-blue-700">
                      Automatically calculated from bedroom breakdown
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="operating_days_per_week" className="block text-sm font-medium text-gray-700">
            Commercial Operating Days per Week <span className="text-red-500">*</span>
          </label>
          <select
            id="operating_days_per_week"
            name="operating_days_per_week"
            required
            value={formData.operating_days_per_week}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7">7 days (Daily operations)</option>
            <option value="6">6 days per week</option>
            <option value="5">5 days per week (Monday-Friday)</option>
            <option value="4">4 days per week</option>
            <option value="3">3 days per week</option>
            <option value="2">2 days per week</option>
            <option value="1">1 day per week</option>
          </select>
          <p className="mt-1 text-sm text-gray-500">How many days per week will commercial waste be generated?</p>
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Continue to Waste Streams'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step2DevelopmentParameters;
